import { useMemo } from 'react';
import { Box } from '@mui/material';
import PlaylistVirtualMediaRow from '@/components/media/playlist-virtual-media-row';
import { getObjectId } from '@/helpers/object.id.helper';
import { resolvePlayTypeForCompositeSlug } from '@/helpers/composite-slug.helper';
import { resolveFlowControlCueSegmentRows } from '@/helpers/flow-control-cue-segment-rows.helper';
import { resolvePlaylistForCueRow } from '@/helpers/simulation-cue-playability.helper';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlLiveState } from '../flow-control-live-state';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';

function virtualSlugFromCueItemId(itemId: string): string | undefined {
  if (itemId.startsWith('virtualMedia:')) {
    return itemId.slice('virtualMedia:'.length);
  }
  if (itemId.startsWith('composite:')) {
    return itemId.slice('composite:'.length);
  }
  return undefined;
}

/** Fallback Control media list from the active cue sheet when the playlist DTO is incomplete. */
export function FlowControlCueSegmentMediaList({
  flow,
  slot,
  segmentKind,
}: {
  flow: FlowDto;
  slot: FlowSlotDto;
  segmentKind: FlowControlSegmentKind;
}) {
  const simulationRows = useFlowControlSimulationCueStore((state) => state.rows);
  const { performMode, cueSheet } = useFlowControlLiveState();
  const rows = performMode === 'play' && cueSheet ? liveCueSheetToSimulationRows(cueSheet) : simulationRows;
  const flowParentId = flow.origin ? getObjectId(flow.origin) : flow.id;

  const compositeRows = useMemo(
    () => resolveFlowControlCueSegmentRows(rows, slot.id, segmentKind),
    [rows, slot.id, segmentKind],
  );

  if (compositeRows.length === 0) {
    return null;
  }

  return (
    <Box>
      {compositeRows.map((row) => {
        const slug = virtualSlugFromCueItemId(row.itemId);
        if (!slug) {
          return null;
        }
        const playlist = resolvePlaylistForCueRow(flow, row);
        if (!playlist?.id) {
          return null;
        }
        return (
          <PlaylistVirtualMediaRow
            key={row.itemId}
            playlist={playlist}
            slug={slug}
            name={row.label ?? slug}
            playType={resolvePlayTypeForCompositeSlug(slug)}
            entityType="Break"
            entityId={slot.id}
            contextParentId={flowParentId}
            contextParentType="Flow"
            displayVariant="flowControl"
            playlistScope="slot"
            playlistSlotId={slot.id}
          />
        );
      })}
    </Box>
  );
}
